import Service from 'react-reservice';

import {
  EColumnPreferenceOrderHistoryType,
  EColumnPreferenceOrderType,
  IColumnSetting,
  IOrderColumnPreferenceV2
} from '@aqx-core/common/api/user-account-setting';

import { compareColumnSettings } from './utils';

export type TCurrentColumnSettingType = EColumnPreferenceOrderType | EColumnPreferenceOrderHistoryType;

export interface IAccountColumnSettingServiceContext {
  getColumnSettingStatus: 'IDLE' | 'LOADING' | 'ERROR' | 'SUCCESS';
  data: {
    [key in EColumnPreferenceOrderType]?: IOrderColumnPreferenceV2;
  };
  updateColumnSettingStatus: 'IDLE' | 'LOADING' | 'ERROR' | 'SUCCESS';
  // Data for editable column settings base on each setting type
  currentColumnSettingType: TCurrentColumnSettingType | undefined;
  fixedColumnSettings: IColumnSetting[];
  editingColumnSettings: (IColumnSetting & { id: string })[];
  editableColumnSettings: (IColumnSetting & { id: string })[];
  defaultColumnSettings: IColumnSetting[];
  isDirty: boolean;
  isResetDefaultButtonDisabled: boolean;
}

const defaultContext: IAccountColumnSettingServiceContext = {
  getColumnSettingStatus: 'LOADING',
  updateColumnSettingStatus: 'IDLE',
  currentColumnSettingType: undefined,
  data: {},
  fixedColumnSettings: [],
  editingColumnSettings: [],
  editableColumnSettings: [],
  defaultColumnSettings: [],
  isResetDefaultButtonDisabled: true,
  isDirty: false
};

class AccountColumnSettingService extends Service<IAccountColumnSettingServiceContext> {
  constructor() {
    super();

    this.context = defaultContext;
  }

  public setGetColumnSettingStatus = (status: IAccountColumnSettingServiceContext['getColumnSettingStatus']) => {
    this.context.getColumnSettingStatus = status;
  };

  public setUpdateColumnSettingStatus = (status: IAccountColumnSettingServiceContext['updateColumnSettingStatus']) => {
    this.context.updateColumnSettingStatus = status;
  };

  public setColumnSettingData = (type: EColumnPreferenceOrderType, preference?: IOrderColumnPreferenceV2) => {
    this.context.data = { ...this.context.data, [type]: preference };
  };

  public initEditData = (type: EColumnPreferenceOrderType | EColumnPreferenceOrderHistoryType) => {
    const { defaultSettings, currentSettings } = this.context.data[type] ?? {};

    if (!defaultSettings || !currentSettings) {
      return;
    }

    this.context.currentColumnSettingType = type;

    this.context.defaultColumnSettings = defaultSettings.filter((item) => item.editable);

    this.context.fixedColumnSettings = currentSettings.filter((item) => !item.editable);
    this.context.editableColumnSettings = currentSettings
      .filter((item) => item.editable)
      .map((item) => ({ ...item, id: item.label }));

    this.context.editingColumnSettings = this.context.editableColumnSettings;

    this.context.isResetDefaultButtonDisabled = compareColumnSettings(
      this.context.editingColumnSettings,
      this.context.defaultColumnSettings
    );
  };

  public updateColumnSettingData = (newSetting: (IColumnSetting & { id: string })[]) => {
    this.context.editingColumnSettings = newSetting;

    this.context.isResetDefaultButtonDisabled = compareColumnSettings(
      this.context.editingColumnSettings,
      this.context.defaultColumnSettings
    );

    this.context.isDirty = !compareColumnSettings(
      this.context.editingColumnSettings,
      this.context.editableColumnSettings
    );
  };

  public resetColumnSettingData = () => {
    this.context.fixedColumnSettings = [];
    this.context.editingColumnSettings = [];
    this.context.defaultColumnSettings = [];
    this.context.isResetDefaultButtonDisabled = true;
    this.context.isDirty = false;
  };
}

export const accountColumnSettingService = new AccountColumnSettingService();
